import { DAY_MS } from "./freshness.js";

// Form badge — a qualitative Ready / Caution / Rest flag for the Home hero,
// derived ONLY from JackedLogs' own logged data (no sensors, no HRV). It is a
// training-load heuristic, NOT an injury or medical prediction — the UI must
// frame it as a gentle flag. The signal mix here is deliberately simple and
// transparent (recovery gap + frequency + an ACWR-style volume spike); the
// weighting is meant to be tuned once RIR/RPE coverage is dense enough to add
// a real session-RPE load term (project_jl_algorithms_post_gate). Lifted from
// apps/mobile into shared with the Home hero surface so mobile + the web-rnw
// console compute one readiness flag.

export type FormZone = "ready" | "caution" | "rest";

export type FormReason =
  | "noData"
  | "wellRecovered"
  | "balanced"
  | "freshStart"
  | "shortRest"
  | "highFrequency"
  | "loadSpike"
  | "longGap";

export type FormBadge = { zone: FormZone; reason: FormReason };

export type FormSession = { startedAtMs: number; volumeKg: number };

export type HomeWeekly = { sessions7d: number; volumeKg7d: number };

/** Rolling last-7-days count + volume for the Home stats card — the same
 *  FormSession feed and 7-day window computeFormBadge reads below (and the
 *  same rolling convention as progressMath.computeSessionSummary, which
 *  serves the Progress footer from full Session rows instead). */
export function computeHomeWeekly(
  sessions: FormSession[],
  nowMs: number,
): HomeWeekly {
  let sessions7d = 0;
  let volumeKg7d = 0;
  for (const s of sessions) {
    const ago = nowMs - s.startedAtMs;
    // Inclusive 7-day boundary, bit-for-bit the computeSessionSummary rule.
    if (ago < 0 || ago > 7 * DAY_MS) continue;
    sessions7d++;
    volumeKg7d += s.volumeKg;
  }
  return { sessions7d, volumeKg7d };
}

// Thresholds (named so the heuristic reads as policy, not magic numbers).
const VERY_HIGH_FREQ_7D = 6; // sessions in 7d that read as overreaching
const HIGH_FREQ_7D = 5;
const BUSY_FREQ_7D = 4;
const ACWR_SPIKE = 1.5; // acute:chronic weekly-volume ratio = clear spike
const ACWR_ELEVATED = 1.3;
const LONG_GAP_DAYS = 14; // detraining / "are you back?" territory
const RECOVERED_DAYS = 2;
const MIN_BASELINE_SESSIONS = 4; // below this the chronic average is unreliable

/**
 * Reduce recent logged sessions to a single readiness flag.
 * @param sessions per-session {startedAtMs, volumeKg}; order-independent.
 * @param nowMs    current time in ms (injected so the result is testable).
 */
export function computeFormBadge(
  sessions: ReadonlyArray<FormSession>,
  nowMs: number,
): FormBadge {
  const recent = sessions
    .filter((s) => s.startedAtMs <= nowMs)
    .sort((a, b) => b.startedAtMs - a.startedAtMs);
  if (recent.length === 0) return { zone: "ready", reason: "noData" };

  const last = recent[0]!;
  const daysSinceLast = (nowMs - last.startedAtMs) / DAY_MS;

  const in7 = recent.filter((s) => nowMs - s.startedAtMs < 7 * DAY_MS);
  const in28 = recent.filter((s) => nowMs - s.startedAtMs < 28 * DAY_MS);
  const count7 = in7.length;
  const vol7 = in7.reduce((sum, s) => sum + s.volumeKg, 0);
  const chronicWeeklyVol = in28.reduce((sum, s) => sum + s.volumeKg, 0) / 4;
  const acwr =
    in28.length >= MIN_BASELINE_SESSIONS && chronicWeeklyVol > 0
      ? vol7 / chronicWeeklyVol
      : null;

  // Rest — a clear "back off" signal.
  if (count7 >= VERY_HIGH_FREQ_7D) return { zone: "rest", reason: "highFrequency" };
  if (acwr !== null && acwr >= ACWR_SPIKE && count7 >= BUSY_FREQ_7D)
    return { zone: "rest", reason: "loadSpike" };

  // Caution — borderline; either pushing hard or an unusual gap.
  if (daysSinceLast >= LONG_GAP_DAYS) return { zone: "caution", reason: "longGap" };
  if (acwr !== null && acwr >= ACWR_ELEVATED)
    return { zone: "caution", reason: "loadSpike" };
  if (count7 >= HIGH_FREQ_7D) return { zone: "caution", reason: "highFrequency" };
  if (daysSinceLast < 0.5 && count7 >= BUSY_FREQ_7D)
    return { zone: "caution", reason: "shortRest" };

  // Ready — adequate recovery, balanced load.
  if (daysSinceLast >= RECOVERED_DAYS)
    return { zone: "ready", reason: "wellRecovered" };
  if (acwr === null) return { zone: "ready", reason: "freshStart" };
  return { zone: "ready", reason: "balanced" };
}
